import { pxToRem, setDataToClipboard } from "@/util/common";
import { Popup, Image, Button, Space } from "antd-mobile";
import { createUseStyles } from "react-jss";
import useCustomTranslation from "@/hooks/useTranslation";
import useCommonFormStyles from "@/hooks/useCommonStyles";
import { isEmpty, isNil } from "lodash";
import { CoinGamingData } from "..";

const CoinGamingPopup: React.FC<{
	visible: boolean;
	data: CoinGamingData;
	onClose?: () => void;
	onSubmit: () => void;
}> = (props) => {
	const classes = useStyles();
	const { t } = useCustomTranslation();
	const commonFormStyles = useCommonFormStyles();
	const isAccessRecords = props.data?.type === "AccessRecords";

	return (
		<Popup
			bodyStyle={{
				borderTopLeftRadius: pxToRem(30),
				borderTopRightRadius: pxToRem(30),
			}}
			maskStyle={{ backgroundColor: "rgba(0, 0, 0, 0.6)" }}
			bodyClassName={classes.popupContent}
			visible={props.visible}
			closeOnMaskClick
			onClose={props.onClose}
		>
			<header className={classes.header}>
				<h2 className={classes.title}>
					<Image
						src={require("@/assets/images/icons/logo_small.png")}
						width={pxToRem(20)}
					/>
					<span>Coin Gaming</span>
				</h2>
				<p className={classes.subTitle}>
					{isAccessRecords
						? isEmpty(props.data?.orderId)
							? t("btnDepositMoney")
							: t("AccessRecords")
						: t("DepositRecords")}
				</p>
				<div
					onClick={() => {
						props.onClose?.();
					}}
					className="close"
				>
					{t("Close")}
				</div>
			</header>
			<section
				style={{
					flex: 1,
					overflowY: "scroll",
				}}
			>
				<div className={classes.total}>
					<span>{t("ArrivalAmount")}</span>
					{props.data?.type === "DepositRecords" ? (
						<span>{t("Completed")}</span>
					) : (
						!isNil(props.data?.status) && (
							<span>
								{[-1, 1].includes(props.data?.status ?? 0)
									? t("Completed")
									: t("Processing")}
							</span>
						)
					)}
				</div>
				<div className={classes.usdtWrap}>
					<Image
						src={require("@/assets/images/icons/USDT.png")}
						className="icon"
					/>
					<span className={isAccessRecords ? "lose" : "win"}>
						{isAccessRecords
							? "-" + props.data?.currency
							: props.data?.currency}
						USDT
					</span>
				</div>
				<ul className={classes.list}>
					<li>
						<dl
							onClick={() => {
								setDataToClipboard(
									props.data?.address ?? "",
									t("CopySuccess")
								);
							}}
						>
							<dt>
								<span>
									{props.data?.type === "AccessRecords"
										? t("WithAddress")
										: t("DepositAddress")}
								</span>
								<Image
									src={require("@/assets/images/icons/copy.png")}
									className="icon"
								/>
							</dt>
							<dd>
								<span>{props.data?.address ?? ""}</span>
							</dd>
						</dl>
					</li>

					<li>
						<dl>
							<dt>
								<span>{t("Time")}</span>
							</dt>
							<dd>
								<span>{props.data?.time ?? ""}</span>
							</dd>
						</dl>
					</li>

					{!isEmpty(props.data?.orderId) &&
					!isEmpty(props.data?.uri) ? (
						<li>
							<dl
								onClick={() => {
									setDataToClipboard(
										props.data?.uri ?? "",
										t("CopySuccess")
									);
								}}
							>
								<dt>
									<span>{t("BlockchainBrowserAddress")}</span>
									<Image
										src={require("@/assets/images/icons/copy.png")}
										className="icon"
									/>
								</dt>
								<dd>
									<span>{props.data?.uri ?? "-"}</span>
								</dd>
							</dl>
						</li>
					) : !isEmpty(props.data?.orderId) ? (
						<li>
							<dl>
								<dt>
									<span>{t("Process")}</span>
								</dt>
								<dd>
									{!isNil(props.data?.status) && (
										<span>
											{[-1, 1].includes(
												props.data?.status ?? 0
											)
												? props.data?.reason
												: t("Auditing")}
										</span>
									)}
								</dd>
							</dl>
						</li>
					) : null}

					<li>
						<dl>
							<dt>
								<span>{t("BlockchainNetwork")}</span>
							</dt>
							<dd>
								<Image
									src={props.data?.logo}
									className="icon"
									fallback={
										<Image
											src={require("@/assets/images/icons/TRON.png")}
											className="icon"
										/>
									}
								/>
								<span>{props.data?.network}</span>
							</dd>
						</dl>
					</li>

					{props.data?.type !== "DepositRecords" && (
						<li>
							<dl>
								<dt>
									<span>{t("WithdrawalNetworkFee")}</span>
								</dt>
								<dd>
									<span>{props.data?.feeRate} USDT</span>
								</dd>
							</dl>
						</li>
					)}
				</ul>

				<Button
					onClick={() => {
						props.onSubmit();
						props.onClose?.();
					}}
					block
					shape="rounded"
					className={commonFormStyles.submit}
				>
					<Space
						style={{
							"--gap": pxToRem(6),
						}}
					>
						<span>{t("Confirm")}</span>
					</Space>
				</Button>
			</section>
		</Popup>
	);
};

export default CoinGamingPopup;

const useStyles = createUseStyles({
	popupContent: {
		display: "flex",
		flexDirection: "column",
		backgroundColor: "#22293F",
		color: "#FFFFFF",
		padding: {
			left: pxToRem(24),
			right: pxToRem(24),
			bottom: pxToRem(42),
		},
		maxHeight: "85vh",
	},
	header: {
		position: "relative",
		padding: {
			top: pxToRem(12),
		},
		textAlign: "center",
		"& .close": {
			position: "absolute",
			left: pxToRem(-10),
			top: 0,
			lineHeight: pxToRem(50),
			padding: {
				left: pxToRem(10),
				right: pxToRem(10),
			},
		},
	},
	title: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		fontSize: pxToRem(15),
		fontWeight: 700,
		color: "#FFFFFF",
		"& span": {
			paddingLeft: pxToRem(4),
		},
	},
	subTitle: {
		color: "#868FA8",
		fontSize: pxToRem(12),
		fontWeight: 500,
		textAlign: "center",
		paddingBottom: pxToRem(4),
	},
	total: {
		display: "flex",
		justifyContent: "space-between",
		fontSize: pxToRem(14),
		fontWeight: 500,
		color: "#868FA8",
		fontFamily: "Noto Sans SC",
		padding: {
			top: pxToRem(6),
			bottom: pxToRem(6),
		},
	},
	usdtWrap: {
		display: "flex",
		alignItems: "center",
		color: "#FFFFFF",
		fontSize: pxToRem(20),
		fontWeight: 600,
		marginBottom: pxToRem(8),
		"& .icon": {
			width: pxToRem(24),
			height: pxToRem(24),
			marginRight: pxToRem(8),
		},
	},
	list: {
		borderRadius: pxToRem(10),
		overflow: "hidden",
		padding: {
			top: pxToRem(3),
			bottom: pxToRem(2),
		},
		marginBottom: pxToRem(44),
		backgroundColor: "#181D2C",
		"& li": {
			display: "flex",
			alignItems: "center",
			color: "#FFFFFF",
			fontSize: pxToRem(16),
			fontWeight: 500,
			backgroundColor: "#181D2C",
			padding: {
				top: pxToRem(20),
				bottom: pxToRem(10),
				left: pxToRem(20),
				right: pxToRem(22),
			},
			borderBottom: "1px solid #2F3A62",
			"&:active": {
				opacity: 0.8,
			},
			"&:last-of-type": {
				borderBottom: "none",
			},
			"& dl": {
				width: "100%",
			},
			"& dt": {
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				color: "#868FA8",
				fontSize: pxToRem(12),
				fontWeight: 400,
				"& .icon": {
					width: pxToRem(14),
					height: pxToRem(14),
				},
			},
			"& dd": {
				display: "flex",
				alignItems: "center",
				color: "#FFFFFF",
				fontSize: pxToRem(12),
				fontWeight: 500,
				lineHeight: pxToRem(34),
				paddingTop: pxToRem(2),
				"& span": {
					flex: 1,
					lineHeight: pxToRem(18),
					whiteSpace: "normal",
					wordBreak: "break-all",
				},
				"& .icon": {
					width: pxToRem(24),
					height: pxToRem(24),
					marginRight: pxToRem(6),
				},
			},
		},
	},
});
